import { useState, useEffect } from 'react';
import { Instagram, Menu, X } from 'lucide-react';
import { NavLink, Link, useLocation } from 'react-router-dom';

const LOGO_URL = 'https://hnmvokfkwldgfrafaehh.supabase.co/storage/v1/object/public/Artists_Graphics/LogoRel.png';
const INSTAGRAM_URL = 'https://www.instagram.com/the_relative_gallery/';

const NAV_ITEMS = [
  { to: '/', label: 'Home', end: true },
  { to: '/artists', label: 'Artists' },
  { to: '/artworks', label: 'Artworks' },
];

function navLinkClass({ isActive }) {
  const base = 'relative text-[11px] font-mono uppercase tracking-[0.2em] transition-colors duration-300 py-1';
  return `${base} ${isActive ? 'text-white' : 'text-[#8a8a8a] hover:text-white'}`;
}

export default function Header() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  // Close mobile menu when the route changes
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="header"
      className="fixed top-0 left-0 right-0 z-50 bg-[#050505]/90 backdrop-blur-md border-b border-[#222222]"
      style={{ height: 'var(--header-height)' }}
    >
      <div className="h-full flex items-center justify-between px-6 sm:px-10 relative">
        {/* Left — desktop nav */}
        <nav className="hidden md:flex items-center gap-8" data-testid="header-nav-desktop">
          {NAV_ITEMS.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              className={navLinkClass}
              data-testid={`header-nav-${n.label.toLowerCase()}`}
            >
              {({ isActive }) => (
                <>
                  {n.label}
                  <span
                    className="absolute left-0 right-0 -bottom-1 h-px bg-white transition-opacity duration-300"
                    style={{ opacity: isActive ? 1 : 0 }}
                  />
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Left — mobile hamburger */}
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="md:hidden -ml-1 p-2 text-[#f6f6f6] hover:text-white transition-colors"
          aria-label={open ? 'Close menu' : 'Open menu'}
          data-testid="header-mobile-toggle"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>

        {/* Centered logo */}
        <Link
          to="/"
          data-testid="header-logo-link"
          className="absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2"
        >
          <img
            src={LOGO_URL}
            alt="The Relative Gallery"
            className="h-10 sm:h-12 md:h-[52px]"
            data-testid="header-logo"
          />
        </Link>

        {/* Right — instagram */}
        <a
          href={INSTAGRAM_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="header-instagram-link"
          className="text-[#f6f6f6] hover:text-white transition-colors duration-300"
          style={{ cursor: 'pointer' }}
        >
          <Instagram className="w-5 h-5" />
        </a>
      </div>

      {/* Mobile menu drawer */}
      <div
        className={`md:hidden fixed left-0 right-0 top-[var(--header-height)] z-50 bg-black border-b border-[#222222] transition-all duration-300 ease-out overflow-hidden ${
          open ? 'max-h-[280px] opacity-100' : 'max-h-0 opacity-0'
        }`}
        data-testid="header-mobile-menu"
      >
        <nav className="flex flex-col px-6 py-4 gap-1">
          {NAV_ITEMS.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              className={({ isActive }) =>
                `py-3 text-[13px] font-mono uppercase tracking-[0.2em] border-b border-[#1a1a1a] transition-colors ${
                  isActive ? 'text-white' : 'text-[#8a8a8a] hover:text-white'
                }`
              }
              data-testid={`header-nav-mobile-${n.label.toLowerCase()}`}
            >
              {n.label}
            </NavLink>
          ))}
        </nav>
      </div>
    </header>
  );
}
